import { SearchExportTypes } from './types';
import { SearchExport } from '@fullstory/proto/fs/services/dex';
import { DataExportActions } from './actions';
import { combineReducers } from '@fullstory/redux-util/utils';
import { filterKeyCombo, makeSearchExportFilterKey } from './utils';
import { int64 } from '@fullstory/proto/google/protobuf';

function exportsById(
  state: Map<string, SearchExport> = new Map<string, SearchExport>(),
  action: DataExportActions,
): Map<string, SearchExport> {
  switch (action.type) {
    case SearchExportTypes.CreateExportSuccess: {
      const newState: Map<string, SearchExport> = new Map(state);
      newState.set(action.data.export!.id, action.data.export!);
      return newState;
    }
    case SearchExportTypes.FetchExportsSuccess: {
      const newState: Map<string, SearchExport> = new Map(state);
      action.data.searchExports.forEach(v => newState.set(v.id, v));
      return newState;
    }
    case SearchExportTypes.GetProgressSuccess: {
      const newState: Map<string, SearchExport> = new Map(state);
      action.data.finished.forEach(v => newState.set(v.id, v));
      action.data.inProgress.forEach(v => {
        const se = newState.get(v.id);
        // continue if the in progress export no longer exists
        if (!se) return;
        se.docsTotalEstimate = v.docsTotalEstimate;
        se.docsProcessedEstimate = v.docsProcessedEstimate;
        if (int64.toNumber(se.docsProcessedEstimate) > 0) {
          // As soon as we see any docs come through, let's assume that the search export is in progress.
          // The state updates helps make other pieces of the UI more accurate if we initially pulled the export
          // down in an unitialized state.
          se.state = SearchExport.State.STATE_IN_PROGRESS;
          se.stateRollup = SearchExport.StateRollup.STATE_ROLLUP_PENDING;
        }
        newState.set(v.id, se);
      });
      return newState;
    }
    case SearchExportTypes.CancelExportSuccess: {
      const newState = new Map(state);
      if (!action.data.searchExport) {
        throw new Error(`Search export action ${action} expected response to be defined`);
      }
      newState.set(action.request.body!.id, action.data.searchExport);
      return newState;
    }
    default:
      return state;
  }
}

function exportIdsByFilter(
  state: Map<string, Set<string>> = new Map<string, Set<string>>(),
  action: DataExportActions,
): Map<string, Set<string>> {
  switch (action.type) {
    case SearchExportTypes.CreateExportSuccess: {
      // When creating and export, we need to append to each appropriate filter map.
      const newState = new Map(state);
      if (!action.data.export) {
        throw new Error(`Search export action ${action} expected response to be defined`);
      }
      const { id, creator, stateRollup } = action.data.export;
      filterKeyCombo({ creator, stateRollup }).forEach(filterKey => {
        newState.has(filterKey)
          ? newState.get(filterKey)!.add(id)
          : newState.set(filterKey, new Set<string>([id]));
      });
      return newState;
    }
    case SearchExportTypes.FetchExportsSuccess: {
      const newState = new Map(state);
      if (!action.request.body) {
        throw new Error(`Search export action ${action} expected body to be defined`);
      }
      const { creator, stateRollup } = action.request.body;
      const filterKey = makeSearchExportFilterKey({ creator, stateRollup });
      newState.has(filterKey)
        ? action.data.searchExports.forEach(se => newState.get(filterKey)!.add(se.id))
        : newState.set(filterKey, new Set<string>(action.data.searchExports.map(se => se.id)));
      return newState;
    }
    case SearchExportTypes.GetProgressSuccess: {
      const newState = new Map(state);
      action.data.finished.forEach(exp => {
        [exp.creator, ''].forEach(creator => {
          newState
            .get(
              makeSearchExportFilterKey({
                creator,
                stateRollup: SearchExport.StateRollup.STATE_ROLLUP_PENDING,
              }),
            )
            ?.delete(exp.id);
          newState
            .get(
              makeSearchExportFilterKey({
                creator,
                stateRollup: SearchExport.StateRollup.STATE_ROLLUP_COMPLETED,
              }),
            )
            ?.add(exp.id);
        });
      });
    }
  }
  return state;
}

type SearchExportsReducerState = {
  exportsById: ReturnType<typeof exportsById>;
  exportIdsByFilter: ReturnType<typeof exportIdsByFilter>;
};

export const searchExportsReducer = combineReducers<SearchExportsReducerState>({
  exportsById,
  exportIdsByFilter,
});
